/**
 * Field tool wire schemas (PROPOSAL — see docs/FIELD_TOOL.md).
 *
 * These describe the data the inspector-side PWA captures and sends to the
 * backend. The backend turns a FieldReport into the same PortalPayload
 * the extension already validates, so the autofill side gets a cleaner input
 * without rewriting.
 *
 * Status: type-only sketch. Not yet wired into routes, not yet validated by
 * the backend. Land this file early so the contract is type-checked across
 * shared, backend, extension, and (future) @cer-autofill/field. Implementation
 * lands incrementally per the phasing in docs/FIELD_TOOL.md.
 */
import { z } from 'zod';
import { InspectionCoverSheetSchema, ItpBatteryEntrySchema, AccreditationCodeSchema, PayloadDiagnosticsSchema, PvAcIsolatorEntrySchema, PvDcIsolatorEntrySchema, PvInverterEntrySchema, PvModuleEntrySchema, } from './cer-portal.js';
/**
 * Stable identifier for one inspector. We use the inspector's name today
 * (`Jason`, `Tim`, …) because that's what Dropbox folder paths use; if we
 * grow more inspectors with the same first name, swap to a UUID.
 */
export const InspectorIdSchema = z
    .string()
    .min(1)
    .transform((s) => s.trim());
/**
 * Device-scoped session token. Stored in IndexedDB; the caller sends it as
 * X-Field-Device-Token on every subsequent call. Opaque to the client.
 *
 * Nothing issues one any more — the endpoint that did was removed along with
 * the passphrase login. The type survives because the auth hook still accepts
 * such a token when AUTH_ALLOW_LEGACY_ADMIN_TOKEN is on, as break-glass.
 */
export const DeviceTokenSchema = z.string().min(32);
/**
 * GPS fix captured client-side via the geolocation API. Optional everywhere
 * (the inspector may have GPS turned off, or be in a roof cavity). Carried
 * for two reasons: (1) future cross-check that the inspection happened at
 * the right address, (2) defence against post-hoc filename forgery on the
 * very rare bad-actor edge case.
 */
export const GpsCoordsSchema = z.object({
    lat: z.number(),
    lng: z.number(),
    accuracyMeters: z.number().optional(),
    capturedAt: z.string(), // naive ISO from the device clock
});
/**
 * Which clause book a response belongs to. Drives which clause-library entry
 * the backend looks up for the canonical question text + canned recommendation.
 */
export const ChecklistSectionSchema = z.enum([
    'cer-pv',
    'battery-section-4-ac',
    'battery-section-5-dc',
]);
/**
 * One portal-compatible Initial Findings Assessment choice for a clause.
 * `value` is the stable machine id persisted by the field tool; `label` is
 * what inspectors see and what the portal currently displays.
 */
export const AssessmentOptionSchema = z.object({
    value: z.string().min(1),
    label: z.string().min(1),
    requiresPhoto: z.boolean().optional(),
    /** No-finding observations exempt from the advisory missing-photo warning. */
    missingPhotoExempt: z.boolean().optional(),
});
/**
 * One clause response captured by the inspector. The clauseId format is
 * section-dependent:
 *   - cer-pv: matches the keys in ChecklistClauseMap (e.g. "SIGN5.1")
 *   - battery-section-*: matches the "Item" column in the CER battery
 *     checklist PDF (e.g. "Pre-assembled BS 78", or the integer "1" for
 *     Documentation 1)
 *
 * Both formats are strings — the backend's clause-library is the source of
 * truth for which IDs are valid in which section.
 */
export const ClauseResponseSchema = z.object({
    section: ChecklistSectionSchema,
    clauseId: z.string().min(1),
    /**
     * Stable Initial Findings Assessment value selected for this clause.
     *
     * New field-tool submissions write this. `outcome` and `severity` remain
     * below as derived compatibility fields for older drafts/submissions and
     * integration code that still expects the legacy shape.
     */
    assessment: z.string().trim().min(1).optional(),
    outcome: z.enum(['Compliant', 'Non-Compliant', 'Not Applicable']),
    /**
     * Legacy compatibility only. The current field UI no longer exposes a
     * standalone severity picker; non-compliant assessment options imply this.
     */
    severity: z.string().trim().min(1).optional(),
    /**
     * Canned wording loaded from the clause library, possibly edited by the
     * inspector. Empty when outcome != Non-Compliant.
     */
    recommendation: z.string().optional(),
    /** Free-text observation from the inspector. */
    inspectorNote: z.string().optional(),
    /** References into FieldReport.photos[].id. */
    photoIds: z.array(z.string().uuid()).default([]),
});
/**
 * Why the inspector did NOT capture a photo for a prompted subject. Lets
 * the admin / tech head see at QA time that the gap is justified, instead
 * of having to chase the inspector.
 */
export const PhotoSkipReasonSchema = z.enum([
    'not-applicable',
    'not-accessible',
    'not-installed',
    'manufacturer-restriction',
    'other',
]);
/**
 * One photo captured against a labelled prompt. `promptKey` maps to either:
 *   - A canonical photo-list entry (e.g. "inverter-nameplate",
 *     "switchboard-door-closed") — see docs/PHOTO_NAMING.md
 *   - A clause-bound evidence photo (e.g. "clause:SIGN5.1") — drives photoIds
 *     on the corresponding ClauseResponse
 *
 * The blob itself is NOT carried in this schema — the PWA uploads photos via
 * POST /api/field/job/:id/photo separately, then references them by id here.
 * That keeps the JSON submit payload small enough to queue offline.
 */
export const FieldPhotoSchema = z.object({
    id: z.string().uuid(),
    promptKey: z.string().min(1),
    filename: z.string().min(1),
    mimeType: z.string().min(1),
    byteSize: z.number().int().nonnegative(),
    capturedAt: z.string(),
    capturedGps: GpsCoordsSchema.optional(),
    uploadStatus: z.enum(['pending', 'uploading', 'uploaded', 'failed']).default('pending'),
});
/**
 * A skipped photo prompt — captured for audit. The admin sees this in the
 * review screen so they know the inspector didn't just forget.
 */
export const FieldPhotoSkipSchema = z.object({
    promptKey: z.string().min(1),
    reason: PhotoSkipReasonSchema,
    note: z.string().optional(),
});
/**
 * Consent record. Captures structured data the existing autofill never had:
 * the homeowner's name, the homeowner signature, the GPS of where the form
 * was signed, and a version pointer to the consent form template the
 * inspector showed the homeowner.
 *
 * Signatures are PNG data URLs at submission time; the backend persists them
 * as separate Dropbox-side image files referenced by URL in the durable
 * record so we're not stashing arbitrary binary in our submission JSON store.
 */
/**
 * Why no documentation was sighted at the time of inspection. Canned from
 * technical-head feedback (Aug 2026) — these three cover nearly every case,
 * and free text is kept for the rest.
 */
export const DocumentationNotSightedReasonSchema = z.enum([
    'home-owner-could-not-find',
    'not-received-from-install-company',
    'home-owner-not-present',
]);
export const DOCUMENTATION_NOT_SIGHTED_REASON_LABELS = {
    'home-owner-could-not-find': 'Home owner could not find at time of inspection',
    'not-received-from-install-company': 'Home owner did not receive from install company',
    'home-owner-not-present': 'Home owner not present at time of inspection',
};
/**
 * Consent as captured on site.
 *
 * The name and signature are deliberately allowed to be EMPTY. The field tool
 * lets an inspector submit with items outstanding — "these travel with the
 * report so the report writer can finish them" — and unsigned consent is one
 * of the things it lists as outstanding. Requiring them here contradicted
 * that: on 2026-08-14 two of Scott's completed battery inspections could not
 * be submitted at all, because `homeownerName` was blank and the parse threw
 * before the request was ever made. He saw a raw validation error and no
 * amount of retrying could have worked.
 *
 * The rest of the system already expects blanks. The QA rule `consent-captured`
 * tests the signature's length and falls back to a scanned T45 PDF, and the
 * dashboard backfills an empty `homeownerName` from the job owner. This schema
 * was the outlier.
 *
 * A signature, WHEN PRESENT, must still be a real PNG data URI — the point is
 * to permit absence, never to accept a malformed or fabricated signature.
 *
 * `signedAt` and `consentFormVersion` follow the same rule (CER-24): Review
 * lists unsigned consent as outstanding, so an empty timestamp or template
 * version must not throw before the report is queued.
 */
export const ConsentRecordSchema = z.object({
    homeownerName: z.string(),
    homeownerSignaturePng: z
        .string()
        .refine((v) => v === '' || v.startsWith('data:image/png;base64,'), {
        message: 'must be empty or a PNG data URI',
    })
        // Old PWA drafts initialised the unsigned state as the bare prefix with an
        // empty payload. Everything downstream keys "unsigned" off '' (the submit
        // route skips the consent PDF, QA's consent-captured needs a payload), and
        // the prefix-only form slipped past the route's `!== ''` guard into the PDF
        // generator, which throws on zero bytes (BTY7036771 et al., 2026-08-24→27).
        // Normalise it here so every parse boundary agrees on one unsigned value.
        .transform((v) => (v === 'data:image/png;base64,' ? '' : v)),
    /** Legacy submissions may carry this, but the current T45 flow does not require it. */
    inspectorSignaturePng: z.string().startsWith('data:image/png;base64,').optional(),
    /**
     * Allowed to be empty. Review lists unsigned consent as outstanding and
     * still lets the inspector submit; requiring a timestamp here contradicted
     * that the same way a required name did (CER-24 / BTY7036771).
     */
    signedAt: z.string(),
    signedAtGps: GpsCoordsSchema.optional(),
    /** e.g. "T45-SRES-PV+BATTERY-v1". Empty when the form was never opened. */
    consentFormVersion: z.string(),
});
/**
 * Full hardware capture from the inspector. Structured at source — no
 * vision-model fallback needed, no nameplate-photo OCR. The inspector can
 * still attach photos via the standard photo-prompt flow.
 */
export const FieldHardwareSchema = z.object({
    inverters: z.array(PvInverterEntrySchema).default([]),
    modules: z.array(PvModuleEntrySchema).default([]),
    acIsolators: z.array(PvAcIsolatorEntrySchema).default([]),
    dcIsolators: z.array(PvDcIsolatorEntrySchema).default([]),
    batteries: z.array(ItpBatteryEntrySchema).default([]),
    panelsInLongestString: z.number().int().positive().optional(),
    numberOfStrings: z.number().int().positive().optional(),
    arrayModuleIndex: z.number().int().nonnegative().optional(),
    totalArrayVoltageOpenCircuitV: z.number().nonnegative().optional(),
    totalArrayCurrentShortCircuitA: z.number().nonnegative().optional(),
    /** Inspector has checked the displayed component details against the installed equipment. */
    verifiedAgainstCer: z.boolean().optional(),
    /**
     * Battery Hardware switchboard questions. Entered by the dashboard operator.
     * Breaker sizes are digits with at most 2 decimal places (`63`, `63.5`);
     * `normalizeCircuitBreakerSize` strips a trailing unit such as "A".
     */
    circuitBreakerSizeMainSwitchGrid: z.string().optional(),
    circuitBreakerSizeMainSwitchInverter: z.string().optional(),
    alternativeSupplyFromBessInverter: z.boolean().optional(),
    switchboardLabellingRequirementsMet: z.boolean().optional(),
});
/** General site/improvement evidence, reviewed with the report recommendations. */
export const GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY = 'general-recommendations';
/**
 * One inspector-side submission for one job.
 *
 * The PWA writes this to IndexedDB as the inspector works, and POSTs it to
 * /api/field/job/:jobId/submit when the device is online and the inspector
 * taps Submit. Photo blobs go up separately via /api/field/job/:id/photo and
 * are referenced from FieldPhoto.id here.
 *
 * Backend uses this to:
 *   1. Synthesise the existing PortalPayload (cached against the JobID; the
 *      extension reads it transparently).
 *   2. Write the conventional Dropbox folder layout (Photos for Report,
 *      Signed Consent form from customer to save, filled checklist PDF).
 *   3. Persist the raw record under field-submissions/<JobID>/<submittedAt>.json
 *      for audit and re-processing.
 */
export const FieldReportSchema = z.object({
    jobId: AccreditationCodeSchema,
    inspectorId: InspectorIdSchema,
    deviceId: z.string().min(1),
    /** Naive ISO from the device clock at submit time. */
    submittedAt: z.string(),
    /** Naive ISO from the device clock when the inspector opened the job. */
    startedAt: z.string(),
    /**
     * Minutes EAST of UTC on the capturing device — `-new Date().getTimezoneOffset()`,
     * so +600 in AEST. Optional: submissions from before M3 don't carry it.
     *
     * The cover sheet's `inspectionStartedAt` / `-EndedAt` are naive local wall
     * clock (the CER portal wants local time, so they must stay that way), while
     * `FieldPhoto.capturedAt` is a UTC instant. Without this offset a server
     * running in UTC resolves the naive stamps to the wrong instant and the
     * `photo-timestamps` QA rule reports every photo as outside the inspection
     * window. Set it whenever the inspection window is stamped.
     */
    deviceUtcOffsetMinutes: z.number().int().optional(),
    cover: InspectionCoverSheetSchema,
    consent: ConsentRecordSchema,
    hardware: FieldHardwareSchema,
    documentation: z
        .object({
        sighted: z.boolean().optional(),
        /**
         * Canned reasons, tickable. Multi-select — "didn't receive it from the
         * installer" and "not present at the time" are routinely both true.
         */
        reasonsNotSighted: z.array(DocumentationNotSightedReasonSchema).optional(),
        /**
         * Free text. Still the only field pre-Aug-2026 submissions carry, so it
         * stays readable/writable — it now backs the "Other" tick box.
         */
        reasonNotSighted: z.string().optional(),
    })
        .optional(),
    checklist: z.array(ClauseResponseSchema).default([]),
    /** Report-level recommendations that are not tied to one checklist clause. */
    generalRecommendations: z.string().optional(),
    /** Inspector-to-reviewer communication; never included in the portal payload. */
    generalNotes: z.string().optional(),
    comboPartnerJobId: AccreditationCodeSchema.optional(),
    nonChecklistResponses: z.record(z.string(), z.string()).optional(),
    /**
     * What the field tool still wanted at the moment the inspector submitted.
     *
     * The device used to BLOCK submit until this list was empty. It no longer
     * does: inspectors routinely finish the walk-around on site and attach
     * photos later, and a report writer can pick up a partial report or send it
     * back for detail. The checks all still run — they are recorded here instead
     * of gating, so the dashboard can show the submission as needs-work rather
     * than ready-for-QA, and can tell "the inspector knew" from "QA found it".
     *
     * Empty array = the inspector submitted a submission the device considered
     * complete. Absent = a pre-Aug-2026 submission, which by definition was
     * complete because it could not have been sent otherwise.
     */
    outstandingAtSubmit: z.array(z.string()).optional(),
    photos: z.array(FieldPhotoSchema).default([]),
    photoSkips: z.array(FieldPhotoSkipSchema).default([]),
    diagnostics: PayloadDiagnosticsSchema.optional(),
});
/**
 * Server response to a successful submission. Echoes what the backend
 * derived so the PWA can show a confirmation summary.
 */
export const FieldReportAckSchema = z.object({
    jobId: AccreditationCodeSchema,
    receivedAt: z.string(),
    photoCount: z.number().int().nonnegative(),
    dropboxFolderPath: z.string().optional(),
    portalPayloadKinds: z.array(z.enum(['cer-pv', 'cer-battery', 'itp-pv', 'itp-battery'])),
    /**
     * False when the report was persisted but the job status transition lost a
     * race or failed — the PWA should still treat the submit as successful.
     */
    caseTransitioned: z.boolean().optional(),
});
/**
 * Inspection type the PWA workflow should branch on for a given job.
 *
 *   - `pv`     — solar PV only (PVD-prefixed JobID)
 *   - `battery` — battery only (BTY-prefixed JobID)
 *   - `combo`  — legacy compatibility for drafts created before linked jobs
 *                were split into independent PVD and BTY field workflows
 *
 * Current API responses classify each JobID independently and use
 * `comboPartnerJobId` only to link/navigate between the two reports.
 */
export const FieldJobInspectionTypeSchema = z.enum(['pv', 'battery', 'combo']);
/**
 * Battery checklist routing hint. The Clean Energy Council/Battery Safety
 * Guide distinction is:
 *   - `integrated-bess-ac` — pre-assembled integrated BESS, supplied with
 *     PCE and AC output for connection to the electrical installation
 *     (AS/NZS 5139 Section 4 checklist).
 *   - `pre-assembled-bs-dc` — pre-assembled battery system, no PCE in the
 *     package, connects to a DC bus/input of external PCE
 *     (AS/NZS 5139 Section 5 checklist).
 *   - `unknown` — show all encoded battery checklists until admin/product
 *     data can classify it.
 */
export const BatteryChecklistKindSchema = z.enum([
    'integrated-bess-ac',
    'pre-assembled-bs-dc',
    'unknown',
]);
/**
 * One row in the inspector's "today's jobs" list. The backend resolves this
 * from a ServiceM8 query (or, in dev, returns sample data).
 */
export const FieldTodaysJobSchema = z.object({
    jobId: AccreditationCodeSchema,
    /** The other JobID when this is a combo job (e.g. BTY paired with this PVD). */
    comboPartnerJobId: AccreditationCodeSchema.optional(),
    inspectionType: FieldJobInspectionTypeSchema,
    /** Optional hint that narrows BTY jobs to Section 4 vs Section 5. */
    batteryChecklistKind: BatteryChecklistKindSchema.optional(),
    siteAddress: z.string().optional(),
    tenderer: z.string().optional(),
    scheduledDate: z.string().optional(),
    /** Booking notes from SM8 — free-text from the admin. */
    bookingNotes: z.string().optional(),
    /**
     * Set on work booked for an EARLIER day that is still unfinished.
     *
     * The roster was scoped to a single date, so an inspection that ran past
     * midnight — or whose device was offline at the end of the day — vanished
     * from the inspector's list while still sitting at `assigned`. Two of
     * Scott's battery jobs on 2026-08-13 were captured in full, 44 photos
     * between them, and then became unreachable in the app the next morning.
     *
     * Carried forward until it is submitted, so unfinished work has to be
     * completed rather than merely found.
     */
    carriedOver: z.boolean().optional(),
});
/**
 * Authoritative job details loaded when an inspector opens a roster entry.
 * The PWA caches this response, then uses `cover` to prefill only fields that
 * are still blank in the local draft so reconnects never clobber field work.
 */
export const FieldJobBootstrapSchema = FieldTodaysJobSchema.extend({
    cover: InspectionCoverSheetSchema,
    hardware: FieldHardwareSchema.optional(),
    source: z.enum(['job-store', 'servicem8', 'job-store+servicem8']),
    /**
     * A field report already stored for this job.
     * `null` means the server looked and there isn't one. Omitted means the
     * lookup did not answer (older servers, or the store read failed).
     */
    existingFieldReport: z
        .object({
        submittedAt: z.string().min(1),
        inspectorId: InspectorIdSchema,
    })
        .nullable()
        .optional(),
    /**
     * Trial switch for the BTY battery location tap. Absent on older servers,
     * which the field app treats as off.
     */
    batteryLocationCapture: z.boolean().optional(),
});
//# sourceMappingURL=field.js.map